import React, { useEffect, useRef, useState } from "react";
import { Button, ProgressBar } from "react-bootstrap";
import { BsBoxSeam } from "react-icons/bs";
import {
  atualizarTipoDroga,
  criarMaterialVazio,
  estadosConservacaoArmaBranca,
  materialParaFormulario,
  participacoes,
  tiposArmaBranca,
  tiposDroga,
  tiposItem,
  unidadesFederativas,
  unidadesOutraDroga,
  validarMaterial,
} from "./materialUtils";

const opcoesVeiculo = {
  especie: ["Passageiro", "Carga", "Misto", "Coleção", "Competição", "Tração", "Especial"],
  tipoVeiculo: [
    "Automóvel", "Bicicleta", "Ciclomotor", "Motocicleta", "Trator", "Elétrico",
    "Caminhão", "Caminhonete", "Camioneta", "Ônibus", "Outro",
  ],
  categoria: ["Particular", "Aluguel", "Oficial", "Aprendizagem", "Representação diplomática"],
  situacaoRegistro: ["registrado", "sem_registro", "nao_identificado"],
  situacaoAdministrativa: [
    "Sem restrições", "Registro de roubo/furto", "Busca e apreensão", "Outra",
  ],
};

function Campo({ id, label, value, onChange, error, required, type = "text", ...props }) {
  return (
    <div className="bo-field">
      <label htmlFor={id}>
        {label}{required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-erro` : undefined}
        {...props}
      />
      {error && <small id={`${id}-erro`} className="material-error" role="alert">{error}</small>}
    </div>
  );
}

function Selecao({ id, label, value, onChange, options, error, required, disabled }) {
  return (
    <div className="bo-field">
      <label htmlFor={id}>
        {label}{required && <span aria-hidden="true"> *</span>}
      </label>
      <select
        id={id}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-erro` : undefined}
        disabled={disabled}
      >
        <option value="">Selecione</option>
        {options.map((option) => {
          const optionValue = Array.isArray(option) ? option[0] : option;
          const optionLabel = Array.isArray(option) ? option[1] : option;
          return <option key={optionValue} value={optionValue}>{optionLabel}</option>;
        })}
      </select>
      {error && <small id={`${id}-erro`} className="material-error" role="alert">{error}</small>}
    </div>
  );
}

function TextoLongo({ id, label, value, onChange, maxLength }) {
  return (
    <div className="bo-field material-full-width">
      <label htmlFor={id}>{label}</label>
      <textarea id={id} value={value ?? ""} maxLength={maxLength} onChange={(event) => onChange(event.target.value)} rows={3} />
    </div>
  );
}

function Participacoes({ values = [], onChange, tipoItem }) {
  const opcoes = tipoItem === "veiculo"
    ? participacoes
    : participacoes.filter(([value]) => value !== "apreendido_infracao_transito");
  return (
    <fieldset className="material-participacoes material-full-width">
      <legend>Participação na ocorrência</legend>
      <div className="material-checkboxes">
        {opcoes.map(([value, label]) => (
          <label key={value}>
            <input
              type="checkbox"
              checked={values.includes(value)}
              onChange={(event) => onChange(
                event.target.checked
                  ? [...values, value]
                  : values.filter((item) => item !== value)
              )}
            />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const getDraft = (boletim) => {
  const draft = boletim.formDrafts?.material;
  if (draft?.item) return materialParaFormulario(draft.item);
  if (draft?.descricao || draft?.quantidade) {
    return materialParaFormulario({ descricao: draft.descricao, quantidade: draft.quantidade });
  }
  return criarMaterialVazio();
};

function AddMaterial({ boletim, setBoletim, onEditIndex, editingIndex }) {
  const [material, setMaterial] = useState(() => getDraft(boletim));
  const [erro, setErro] = useState(null);
  const editingIndexAnterior = useRef(editingIndex);

  useEffect(() => {
    setBoletim((current) => ({
      ...current,
      formDrafts: {
        ...current.formDrafts,
        material: { item: material },
      },
    }));
  }, [material, setBoletim]);

  const atualizarCampo = (secao, campo, valor) => {
    setMaterial((current) => ({
      ...current,
      [secao]: { ...current[secao], [campo]: valor },
    }));
    setErro(null);
  };
  const dados = material.tipoItem
    ? material[material.tipoItem === "veiculo" ? "dadosVeiculo" :
      material.tipoItem === "arma_fogo" ? "dadosArma" :
      material.tipoItem === "arma_branca" ? "dadosArmaBranca" :
      material.tipoItem === "droga" ? "dadosDroga" : "dadosObjeto"] || {}
    : {};
  const secao = material.tipoItem === "veiculo" ? "dadosVeiculo" :
    material.tipoItem === "arma_fogo" ? "dadosArma" :
      material.tipoItem === "arma_branca" ? "dadosArmaBranca" :
      material.tipoItem === "droga" ? "dadosDroga" :
        material.tipoItem === "outro_objeto" ? "dadosObjeto" : "";
  const erroCampo = (campo) => erro?.campo === campo ? erro.mensagem : undefined;

  const adicionar = (event) => {
    event.preventDefault();
    const validacao = validarMaterial(material);
    if (validacao) {
      setErro(validacao);
      document.getElementById(validacao.campo.replace(".", "-"))?.focus();
      return;
    }

    setBoletim((current) => {
      const itens = [...(current.materiaisApreendidos || [])];
      if (editingIndex === null || editingIndex === undefined) {
        itens.push(material);
      } else {
        const anterior = { ...itens[editingIndex] };
        delete anterior.dadosVeiculo;
        delete anterior.dadosArma;
        delete anterior.dadosArmaBranca;
        delete anterior.dadosDroga;
        delete anterior.dadosObjeto;
        itens[editingIndex] = { ...anterior, ...material };
      }
      return {
        ...current,
        materiaisApreendidos: itens,
        formDrafts: { ...current.formDrafts, material: { item: criarMaterialVazio() } },
      };
    });
    setMaterial(criarMaterialVazio());
    setErro(null);
    onEditIndex(null);
  };

  useEffect(() => {
    if (editingIndexAnterior.current === editingIndex) return;
    editingIndexAnterior.current = editingIndex;
    if (editingIndex !== null && editingIndex !== undefined) {
      const item = boletim.materiaisApreendidos?.[editingIndex];
      if (item) {
        setMaterial(materialParaFormulario(item));
        setErro(null);
      }
    } else {
      setMaterial(criarMaterialVazio());
      setErro(null);
    }
  }, [editingIndex, boletim.materiaisApreendidos]);

  const cancelarEdicao = () => {
    setMaterial(criarMaterialVazio());
    setErro(null);
    onEditIndex(null);
  };

  return (
    <div className="bo-section">
      <ProgressBar now={60} className="bo-progress" />
      <form onSubmit={adicionar} noValidate>
        <div className="bo-section-title">
          {editingIndex === null || editingIndex === undefined ? "Adicionar item" : "Editar item"}
        </div>
        <div className="material-grid">
          <Selecao
            id="tipoItem"
            label="Tipo de item"
            value={material.tipoItem}
            required
            error={erroCampo("tipoItem")}
            options={tiposItem}
            onChange={(tipoItem) => {
              setMaterial(criarMaterialVazio(tipoItem));
              setErro(null);
            }}
          />
        </div>

        {material.tipoItem === "veiculo" && (
          <>
            <div className="material-section-heading">Identificação do veículo</div>
            <div className="material-grid">
              <Campo id="dadosVeiculo-pais" label="País" value={dados.pais} onChange={(v) => atualizarCampo(secao, "pais", v)} />
              <Campo id="dadosVeiculo-placa" label="Placa" value={dados.placa} error={erroCampo("dadosVeiculo.placa")} onChange={(v) => atualizarCampo(secao, "placa", v)} placeholder="Padrão antigo ou Mercosul" />
              <Selecao id="dadosVeiculo-uf" label="UF" value={dados.uf} options={unidadesFederativas} onChange={(v) => atualizarCampo(secao, "uf", v)} />
              <Campo id="dadosVeiculo-renavam" label="RENAVAM" value={dados.renavam} onChange={(v) => atualizarCampo(secao, "renavam", v)} />
              <Campo id="dadosVeiculo-chassi" label="Chassi" value={dados.chassi} error={erroCampo("dadosVeiculo.chassi")} required={dados.situacaoRegistro === "sem_registro"} onChange={(v) => atualizarCampo(secao, "chassi", v)} />
              <Campo id="dadosVeiculo-municipioRegistro" label="Município de registro" value={dados.municipioRegistro} onChange={(v) => atualizarCampo(secao, "municipioRegistro", v)} />
              <Selecao
                id="dadosVeiculo-situacaoRegistro"
                label="Situação de registro"
                value={dados.situacaoRegistro}
                required
                error={erroCampo("dadosVeiculo.situacaoRegistro")}
                options={opcoesVeiculo.situacaoRegistro.map((value) => [value, value === "nao_identificado" ? "Identificação indisponível" : value === "sem_registro" ? "Sem registro" : "Registrado"])}
                onChange={(v) => atualizarCampo(secao, "situacaoRegistro", v)}
              />
              <Selecao id="dadosVeiculo-especie" label="Espécie" value={dados.especie} options={opcoesVeiculo.especie} onChange={(v) => atualizarCampo(secao, "especie", v)} />
              <Selecao id="dadosVeiculo-tipoVeiculo" label="Tipo de veículo" value={dados.tipoVeiculo} options={opcoesVeiculo.tipoVeiculo} onChange={(v) => atualizarCampo(secao, "tipoVeiculo", v)} />
              <Campo id="dadosVeiculo-marca" label="Marca" value={dados.marca} onChange={(v) => atualizarCampo(secao, "marca", v)} />
              <Campo id="dadosVeiculo-modelo" label="Modelo" value={dados.modelo} onChange={(v) => atualizarCampo(secao, "modelo", v)} />
              <Selecao id="dadosVeiculo-categoria" label="Categoria" value={dados.categoria} options={opcoesVeiculo.categoria} onChange={(v) => atualizarCampo(secao, "categoria", v)} />
              <Campo id="dadosVeiculo-corPredominante" label="Cor predominante" value={dados.corPredominante} onChange={(v) => atualizarCampo(secao, "corPredominante", v)} />
              <Campo id="dadosVeiculo-anoFabricacao" label="Ano de fabricação" value={dados.anoFabricacao} onChange={(v) => atualizarCampo(secao, "anoFabricacao", v)} inputMode="numeric" />
              <Campo id="dadosVeiculo-anoModelo" label="Ano-modelo" value={dados.anoModelo} onChange={(v) => atualizarCampo(secao, "anoModelo", v)} inputMode="numeric" />
              <Selecao id="dadosVeiculo-situacaoAdministrativa" label="Situação administrativa/criminal" value={dados.situacaoAdministrativa} options={opcoesVeiculo.situacaoAdministrativa} onChange={(v) => atualizarCampo(secao, "situacaoAdministrativa", v)} />
              <Campo id="dadosVeiculo-proprietario" label="Proprietário" value={dados.proprietario} onChange={(v) => atualizarCampo(secao, "proprietario", v)} />
              <Participacoes tipoItem={material.tipoItem} values={dados.participacoes} onChange={(v) => atualizarCampo(secao, "participacoes", v)} />
            </div>
          </>
        )}

        {material.tipoItem === "arma_fogo" && (
          <>
            <div className="material-section-heading">Identificação da arma</div>
            <div className="material-grid">
              <Campo id="dadosArma-especie" label="Espécie" value={dados.especie} required error={erroCampo("dadosArma.especie")} onChange={(v) => atualizarCampo(secao, "especie", v)} placeholder="Ex.: revólver, pistola" />
              <Campo id="quantidade" label="Quantidade de armas (se conhecida)" type="number" min="1" step="1" value={material.quantidade ?? ''} error={erroCampo('quantidade')} onChange={value => { setMaterial(current => ({ ...current, quantidade: value })); setErro(null); }} placeholder="Não confundir com quantidade de canos" />
              <Campo id="dadosArma-marca" label="Marca" value={dados.marca} onChange={(v) => atualizarCampo(secao, "marca", v)} />
              <Campo id="dadosArma-modelo" label="Modelo" value={dados.modelo} onChange={(v) => atualizarCampo(secao, "modelo", v)} />
              <Campo id="dadosArma-acabamento" label="Acabamento" value={dados.acabamento} onChange={(v) => atualizarCampo(secao, "acabamento", v)} />
              <Campo id="dadosArma-quantidadeCanos" label="Quantidade de canos" value={dados.quantidadeCanos} onChange={(v) => atualizarCampo(secao, "quantidadeCanos", v)} type="number" min="1" step="1" error={erroCampo("dadosArma.quantidadeCanos")} />
              <Campo id="dadosArma-fabricacao" label="Fabricação" value={dados.fabricacao} onChange={(v) => atualizarCampo(secao, "fabricacao", v)} />
              <Selecao id="dadosArma-tipoUso" label="Tipo de uso" value={dados.tipoUso} options={["Permitido", "Restrito"].map((v) => [v.toLowerCase(), v])} onChange={(v) => atualizarCampo(secao, "tipoUso", v)} />
              <Campo id="dadosArma-calibreReal" label="Calibre real" value={dados.calibreReal} onChange={(v) => atualizarCampo(secao, "calibreReal", v)} />
              <Campo id="dadosArma-numeroRegistro" label="Número SIGMA ou SINARM" value={dados.numeroRegistro} onChange={(v) => atualizarCampo(secao, "numeroRegistro", v)} />
              <Selecao
                id="dadosArma-situacaoNumeroSerie"
                label="Situação do número de série"
                value={dados.situacaoNumeroSerie}
                options={[["informado", "Informado"], ["suprimido", "Suprimido"], ["nao_identificado", "Não foi possível identificar"]]}
                onChange={(v) => {
                  setMaterial((current) => ({
                    ...current,
                    dadosArma: {
                      ...current.dadosArma,
                      situacaoNumeroSerie: v,
                      ...(["suprimido", "nao_identificado"].includes(v) ? { numeroSerie: "" } : {}),
                    },
                  }));
                  setErro(null);
                }}
              />
              {!["suprimido", "nao_identificado"].includes(dados.situacaoNumeroSerie) && (
                <Campo id="dadosArma-numeroSerie" label="Número de série" value={dados.numeroSerie} required error={erroCampo("dadosArma.numeroSerie")} onChange={(v) => atualizarCampo(secao, "numeroSerie", v)} />
              )}
              <Participacoes tipoItem={material.tipoItem} values={dados.participacoes} onChange={(v) => atualizarCampo(secao, "participacoes", v)} />
              <label className="material-checkbox material-full-width">
                <input type="checkbox" checked={Boolean(dados.informacoesCarga)} onChange={(e) => atualizarCampo(secao, "informacoesCarga", e.target.checked)} />
                A arma faz parte de um carregamento
              </label>
            </div>
          </>
        )}

        {material.tipoItem === "arma_branca" && (
          <>
            <div className="material-section-heading">Identificação da arma branca</div>
            <div className="material-grid">
              <Selecao
                id="dadosArmaBranca-tipoArmaBranca"
                label="Tipo de arma branca"
                value={dados.tipoArmaBranca}
                required
                error={erroCampo("dadosArmaBranca.tipoArmaBranca")}
                options={tiposArmaBranca}
                onChange={(v) => atualizarCampo(secao, "tipoArmaBranca", v)}
              />
              <Campo
                id="dadosArmaBranca-quantidade"
                label="Quantidade"
                value={dados.quantidade}
                required
                error={erroCampo("dadosArmaBranca.quantidade")}
                onChange={(v) => atualizarCampo(secao, "quantidade", v)}
                type="number"
                min="1"
                step="1"
              />
              <Campo
                id="dadosArmaBranca-descricao"
                label="Descrição do objeto"
                value={dados.descricao}
                maxLength={500}
                error={erroCampo("dadosArmaBranca.descricao")}
                onChange={(v) => atualizarCampo(secao, "descricao", v)}
              />
              <Campo
                id="dadosArmaBranca-marca"
                label="Marca ou fabricante"
                value={dados.marca}
                maxLength={120}
                error={erroCampo("dadosArmaBranca.marca")}
                onChange={(v) => atualizarCampo(secao, "marca", v)}
              />
              <Campo
                id="dadosArmaBranca-materialFabricacao"
                label="Material de fabricação"
                value={dados.materialFabricacao}
                maxLength={120}
                error={erroCampo("dadosArmaBranca.materialFabricacao")}
                onChange={(v) => atualizarCampo(secao, "materialFabricacao", v)}
                placeholder="Ex.: metal, madeira"
              />
              <Campo
                id="dadosArmaBranca-comprimentoAproximadoCm"
                label="Comprimento aproximado (cm)"
                value={dados.comprimentoAproximadoCm}
                error={erroCampo("dadosArmaBranca.comprimentoAproximadoCm")}
                onChange={(v) => atualizarCampo(secao, "comprimentoAproximadoCm", v)}
                type="number"
                min="0.01"
                step="any"
              />
              <Selecao
                id="dadosArmaBranca-estadoConservacao"
                label="Estado de conservação"
                value={dados.estadoConservacao}
                options={estadosConservacaoArmaBranca}
                error={erroCampo("dadosArmaBranca.estadoConservacao")}
                onChange={(v) => atualizarCampo(secao, "estadoConservacao", v)}
              />
              <TextoLongo
                id="dadosArmaBranca-caracteristicasDistintivas"
                label="Características distintivas"
                value={dados.caracteristicasDistintivas}
                maxLength={500}
                onChange={(v) => atualizarCampo(secao, "caracteristicasDistintivas", v)}
              />
              <TextoLongo
                id="dadosArmaBranca-observacoes"
                label="Observações"
                value={dados.observacoes}
                maxLength={1000}
                onChange={(v) => atualizarCampo(secao, "observacoes", v)}
              />
              <Participacoes tipoItem={material.tipoItem} values={dados.participacoes} onChange={(v) => atualizarCampo(secao, "participacoes", v)} />
            </div>
          </>
        )}

        {material.tipoItem === "droga" && (
          <>
            <div className="material-section-heading">Identificação da droga</div>
            <div className="material-grid">
              <Selecao id="dadosDroga-tipoDroga" label="Tipo de droga" value={dados.tipoDroga} required error={erroCampo("dadosDroga.tipoDroga")} options={tiposDroga} onChange={(v) => { setMaterial((current) => atualizarTipoDroga(current, v)); setErro(null); }} />
              {dados.tipoDroga === "Outra droga" && (
                <Campo id="dadosDroga-substancia" label="Qual substância?" value={dados.substancia} required error={erroCampo("dadosDroga.substancia")} onChange={(v) => atualizarCampo(secao, "substancia", v)} />
              )}
              <Campo id="dadosDroga-quantidade" label="Quantidade" value={dados.quantidade} required error={erroCampo("dadosDroga.quantidade")} onChange={(v) => atualizarCampo(secao, "quantidade", v)} type="number" min="0.01" step="any" />
              {dados.tipoDroga === "Outra droga" ? (
                <Selecao id="dadosDroga-unidade" label="Unidade de medida" value={dados.unidade} required error={erroCampo("dadosDroga.unidade")} options={unidadesOutraDroga} onChange={(v) => atualizarCampo(secao, "unidade", v)} />
              ) : (
                <Campo id="dadosDroga-unidade" label="Unidade de medida" value={dados.unidade} readOnly disabled />
              )}
              <Participacoes tipoItem={material.tipoItem} values={dados.participacoes} onChange={(v) => atualizarCampo(secao, "participacoes", v)} />
              <label className="material-checkbox material-full-width">
                <input type="checkbox" checked={Boolean(dados.informacoesCarga)} onChange={(e) => atualizarCampo(secao, "informacoesCarga", e.target.checked)} />
                A droga faz parte de um carregamento
              </label>
              <TextoLongo id="dadosDroga-observacoes" label="Observações" value={dados.observacoes} onChange={(v) => atualizarCampo(secao, "observacoes", v)} />
            </div>
          </>
        )}

        {material.tipoItem === "outro_objeto" && (
          <>
            <div className="material-section-heading">Identificação do objeto/bem</div>
            <div className="material-grid">
              <Campo id="dadosObjeto-tipoObjeto" label="Tipo de objeto" value={dados.tipoObjeto} required error={erroCampo("dadosObjeto.tipoObjeto")} onChange={(v) => atualizarCampo(secao, "tipoObjeto", v)} placeholder="Ex.: bicicleta, notebook, celular" />
              <Campo id="dadosObjeto-quantidade" label="Quantidade" value={dados.quantidade} required error={erroCampo("dadosObjeto.quantidade")} onChange={(v) => atualizarCampo(secao, "quantidade", v)} type="number" min="0.01" step="any" />
              <Campo id="dadosObjeto-unidade" label="Unidade de medida" value={dados.unidade} required error={erroCampo("dadosObjeto.unidade")} onChange={(v) => atualizarCampo(secao, "unidade", v)} placeholder="Ex.: unidade" />
              <Participacoes tipoItem={material.tipoItem} values={dados.participacoes} onChange={(v) => atualizarCampo(secao, "participacoes", v)} />
              <TextoLongo id="dadosObjeto-observacoes" label="Observações" value={dados.observacoes} onChange={(v) => atualizarCampo(secao, "observacoes", v)} />
            </div>
          </>
        )}

        {erro?.campo === "tipoItem" && (
          <div className="material-error material-form-error" role="alert">{erro.mensagem}</div>
        )}
        <div className="material-actions">
          <Button variant="warning" type="submit">
            <BsBoxSeam size={20} className="me-2" />
            {editingIndex === null || editingIndex === undefined ? "Adicionar item" : "Salvar alterações"}
          </Button>
          {editingIndex !== null && editingIndex !== undefined && (
            <Button variant="outline-secondary" type="button" onClick={cancelarEdicao}>Cancelar edição</Button>
          )}
        </div>
      </form>
    </div>
  );
}

export default AddMaterial;
